import React, { useCallback, useEffect, useState } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { useUserInfo } from '@/shared/hooks/useUserInfo';
import { holidayApi } from '@/features/policy/holidayManagement/api/HolidayApi';
import {
    DEFAULT_HOLIDAY_FORM,
    DOW_LABELS,
    HolidayDto,
    HolidayForm,
    SaveHolidayRequest,
} from '@/features/policy/holidayManagement/types/holiday.types';
import '../../slotManagement/styles/SlotManagement.css';
import ConfirmModal from "@/shared/components/feedback/ConfirmModal";
import Pagination from "@/shared/components/dataDisplay/Pagination";

const CURRENT_YEAR = String(new Date().getFullYear());
const PAGE_SIZE = 15;

const HolidayManagementPage: React.FC = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [holidays, setHolidays] = useState<HolidayDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [yearFilter, setYearFilter] = useState(CURRENT_YEAR);

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState<HolidayForm>({ ...DEFAULT_HOLIDAY_FORM });
    const [saving, setSaving] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState<HolidayDto | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [nameFilter, setNameFilter] = useState('');

    // ===== 조회 =====

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            const res = await holidayApi.getHolidays(yearFilter || undefined);
            setHolidays(res.holidays ?? []);
            setCurrentPage(1);
        } catch {
            showToast('공휴일 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast, yearFilter]);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    // ===== 등록/수정 =====

    const handleOpenAdd = () => {
        setForm({ ...DEFAULT_HOLIDAY_FORM });
        setModalOpen(true);
    };

    const handleOpenEdit = (holiday: HolidayDto) => {
        setForm({
            seqNo: holiday.seqNo,
            holidayDate: holiday.holidayDate,
            holidayName: holiday.holidayName,
            targetDow: String(holiday.targetDow),
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!form.holidayDate.trim()) {
            alert('날짜는 필수입니다.');
            return;
        }
        if (!form.holidayName.trim()) {
            alert('공휴일명은 필수입니다.');
            return;
        }
        if (form.targetDow === '') {
            alert('적용 요일을 선택해주세요.');
            return;
        }

        const userInfo = getUserInfo();
        if (!userInfo) return;

        const req: SaveHolidayRequest = {
            seqNo: form.seqNo,
            holidayDate: form.holidayDate.trim(),
            holidayName: form.holidayName.trim(),
            targetDow: Number(form.targetDow),
            userId: userInfo.userId,
        };

        setSaving(true);
        try {
            await holidayApi.saveHoliday(req);
            showToast(form.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            setModalOpen(false);
            fetchHolidays();
        } catch (error: any) {
            const msg = error?.message || '저장에 실패했습니다.';
            showToast(msg, { type: ToastType.ERROR });
        } finally {
            setSaving(false);
        }
    };

    // ===== 삭제 =====

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await holidayApi.deleteHoliday(deleteTarget.seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            setDeleteTarget(null);
            fetchHolidays();
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    // ===== 자동 동기화 =====

    const handleSync = async () => {
        const year = Number(yearFilter || CURRENT_YEAR);
        if (!year || isNaN(year)) {
            alert('연도를 입력해주세요.');
            return;
        }
        const userInfo = getUserInfo();
        if (!userInfo) return;

        setSyncing(true);
        try {
            const res = await holidayApi.syncKoreanHolidays(year, userInfo.userId);
            showToast(`${year}년 공휴일 동기화 완료 (${res.savedCount}건 추가/복구)`, { type: ToastType.SUCCESS });
            fetchHolidays();
        } catch (error: any) {
            showToast(error?.message || '동기화에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSyncing(false);
        }
    };

    // ===== 헬퍼 =====

    const setField = (key: keyof HolidayForm, value: string) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) setModalOpen(false);
    };

    const dowLabel = (dow: number) => DOW_LABELS[dow - 1] ?? '-';

    const filteredHolidays = (nameFilter.trim()
            ? holidays.filter(h => h.holidayName.includes(nameFilter.trim()))
            : holidays
    ).slice().sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
    const totalPages = Math.ceil(filteredHolidays.length / PAGE_SIZE);
    const pagedHolidays = filteredHolidays.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div style={{ padding: '24px' }}>
            {/* 헤더 */}
            <div className="slot-page-header">
                <h1 className="slot-page-title">공휴일 관리</h1>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="number"
                        value={yearFilter}
                        onChange={e => setYearFilter(e.target.value)}
                        placeholder="연도"
                        style={{
                            width: '90px',
                            padding: '9px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                        }}
                    />
                    <input
                        type="text"
                        value={nameFilter}
                        onChange={e => { setNameFilter(e.target.value); setCurrentPage(1); }}
                        placeholder="공휴일명 검색"
                        style={{
                            width: '160px',
                            padding: '9px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                        }}
                    />
                    <button
                        className="btn-modal-cancel"
                        onClick={handleSync}
                        disabled={syncing}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {syncing ? '동기화 중...' : '자동 동기화'}
                    </button>
                    <button className="slot-add-btn" onClick={handleOpenAdd}>
                        + 공휴일 추가
                    </button>
                </div>
            </div>

            {/* 테이블 */}
            <div className="slot-table-wrap">
                {loading ? (
                    <div className="slot-empty">불러오는 중...</div>
                ) : filteredHolidays.length === 0 ? (
                    <div className="slot-empty">
                        {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 공휴일이 없습니다.'}
                    </div>
                ): (
                    <table className="slot-table">
                        <thead>
                        <tr>
                            <th>날짜</th>
                            <th>공휴일명</th>
                            <th>적용 요일</th>
                            <th>관리</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagedHolidays.map(h => (
                            <tr key={h.seqNo}>
                                <td>{h.holidayDate}</td>
                                <td className="text-left">{h.holidayName}</td>
                                <td>{dowLabel(h.targetDow)}</td>
                                <td>
                                    <button className="btn-edit" onClick={() => handleOpenEdit(h)}>수정</button>
                                    <button className="btn-delete" onClick={() => setDeleteTarget(h)}>삭제</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            {/* 등록/수정 모달 */}
            {modalOpen && (
                <div className="slot-modal-overlay" onClick={handleBackdrop}>
                    <div className="slot-modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
                        <div className="slot-modal-header">
                            <h2 className="slot-modal-title">
                                {form.seqNo ? '공휴일 수정' : '공휴일 등록'}
                            </h2>
                        </div>

                        <div className="slot-modal-body">
                            <div className="slot-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                <div className="slot-form-group">
                                    <label className="slot-form-label required">날짜 (YYYY-MM-DD)</label>
                                    <input
                                        className="slot-form-input"
                                        type="date"
                                        value={form.holidayDate}
                                        onChange={e => setField('holidayDate', e.target.value)}
                                    />
                                </div>

                                <div className="slot-form-group">
                                    <label className="slot-form-label required">공휴일명</label>
                                    <input
                                        className="slot-form-input"
                                        type="text"
                                        placeholder="예) 신정"
                                        value={form.holidayName}
                                        onChange={e => setField('holidayName', e.target.value)}
                                    />
                                </div>

                                <div className="slot-form-group">
                                    <label className="slot-form-label required">적용 요일 (슬롯 목표 기준)</label>
                                    <select
                                        className="slot-form-select"
                                        value={form.targetDow}
                                        onChange={e => setField('targetDow', e.target.value)}
                                    >
                                        {DOW_LABELS.map((label, idx) => (
                                            <option key={idx + 1} value={idx + 1}>{label}</option>
                                        ))}
                                    </select>
                                    <span className="slot-form-hint">
                                        이 공휴일에 적용할 목표 건수 기준 요일 (보통 토=5 또는 일=6)
                                    </span>
                                </div>

                            </div>
                        </div>

                        <div className="slot-modal-footer">
                            <button className="btn-modal-cancel" onClick={() => setModalOpen(false)}>
                                취소
                            </button>
                            <button
                                className="btn-modal-save"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 삭제 확인 모달 */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                title="공휴일 삭제"
                message={`'${deleteTarget?.holidayName}' (${deleteTarget?.holidayDate})을 삭제하시겠습니까?`}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default HolidayManagementPage;
